import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";

const API = process.env.REACT_APP_BACKEND_URL;
const HEARTBEAT_INTERVAL = 15000;
const VISITOR_ID_KEY = "jtr_visitor_id";
const SESSION_ID_KEY = "jtr_session_id";
const FIRST_TOUCH_KEY = "jtr_first_touch";

function getOrCreateId(key, storage) {
  let id = storage.getItem(key);
  if (!id) {
    id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    storage.setItem(key, id);
  }
  return id;
}

/**
 * VisitorBeacon — invisible page-level tracker.
 * Mounts inside <AuthProvider> so it can read the current user.
 * Sends heartbeats every 30s + a pageview event on every URL change.
 * Skipped entirely for admin/host accounts (we don't track our own staff).
 */
export default function VisitorBeacon() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const lastUrl = useRef("");
  const intervalRef = useRef(null);

  const visitorId = getOrCreateId(VISITOR_ID_KEY, localStorage);
  const sessionId = getOrCreateId(SESSION_ID_KEY, sessionStorage);

  const isAdmin = user?.role === "admin" || user?.role === "host";
  // Skip ALL beacon traffic until auth has resolved AND we know the role.
  // This closes the race where the heartbeat fired during the initial null-user
  // window before the admin role was loaded. Also skip the private,
  // synthetic-content-only /design-preview reference build (2026-02) — it's
  // not a real visitor journey and shouldn't pollute visitor analytics.
  const skip = loading || isAdmin || location.pathname.startsWith("/design-preview");
  const isHeadless = /HeadlessChrome|Playwright|Puppeteer|PhantomJS|Selenium/i.test(
    navigator.userAgent || ""
  );

  const getUTMParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
      utm_content: params.get("utm_content") || "",
      utm_term: params.get("utm_term") || "",
    };
  };

  const getFirstTouch = () => {
    let ft = null;
    try {
      ft = JSON.parse(localStorage.getItem(FIRST_TOUCH_KEY) || "null");
    } catch {
      ft = null;
    }
    if (!ft) {
      const utm = getUTMParams();
      const hasUTM = Object.values(utm).some((v) => v);
      ft = {
        ...utm,
        landing_page: window.location.pathname + window.location.search,
        referrer: document.referrer || "",
        timestamp: new Date().toISOString(),
      };
      if (hasUTM || document.referrer) {
        try {
          localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(ft));
        } catch {
          /* storage full / disabled */
        }
      }
    }
    return ft;
  };

  const getPayload = () => {
    const utm = getUTMParams();
    const firstTouch = getFirstTouch();
    return {
      visitor_id: visitorId,
      session_id: sessionId,
      url: window.location.pathname + window.location.search,
      title: document.title,
      referrer: document.referrer,
      landing_page: window.location.pathname,
      member_id: user?.id || null,
      member_email: user?.email || null,
      member_name: user
        ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email
        : null,
      device: navigator.userAgent?.slice(0, 200),
      screen_size: `${window.innerWidth}x${window.innerHeight}`,
      screen_resolution: `${window.screen?.width}x${window.screen?.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      language: navigator.language || "",
      languages: navigator.languages?.slice(0, 5)?.join(",") || "",
      platform: navigator.platform || "",
      hardware_concurrency: navigator.hardwareConcurrency || 0,
      color_depth: window.screen?.colorDepth || 0,
      touch_support: (navigator.maxTouchPoints || 0) > 0,
      is_automation: isHeadless,
      automation_type: isHeadless ? "emergent_ai" : null,
      ...utm,
      first_touch: firstTouch,
    };
  };

  const sendBeacon = (endpoint, data) => {
    if (skip) return;
    try {
      const blob = new Blob([JSON.stringify(data)], { type: "application/json" });
      const ok = navigator.sendBeacon(`${API}/api/beacon/${endpoint}`, blob);
      if (ok) return;
    } catch {
      /* fall through */
    }
    fetch(`${API}/api/beacon/${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
      keepalive: true,
    }).catch(() => {});
  };

  // Pageview on URL change. `useLocation()` re-renders the component on
  // every SPA navigation, which is the *only* reliable signal in a
  // React-Router app (parent re-renders don't propagate down on
  // <Route> swaps). We also fire an immediate heartbeat so the
  // admin Live tab reflects the new page within the next 5s poll,
  // not after the 15s heartbeat tick.
  useEffect(() => {
    if (skip) return;
    const currentUrl = location.pathname + location.search;
    if (currentUrl !== lastUrl.current) {
      lastUrl.current = currentUrl;
      const payload = getPayload();
      sendBeacon("pageview", payload);
      sendBeacon("heartbeat", payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, skip]);

  // Heartbeat every 30s.
  useEffect(() => {
    if (skip) return undefined;
    const heartbeat = () => sendBeacon("heartbeat", getPayload());
    heartbeat();
    intervalRef.current = setInterval(heartbeat, HEARTBEAT_INTERVAL);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, skip]);

  // Login event when user transitions to logged in.
  useEffect(() => {
    if (skip) return;
    if (user?.id) {
      sendBeacon("event", { ...getPayload(), event_type: "login" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, skip]);

  // Page-leave event.
  useEffect(() => {
    if (skip) return undefined;
    const handleUnload = () =>
      sendBeacon("event", { ...getPayload(), event_type: "page_leave" });
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip]);

  // Click tracking — every meaningful click capture (link, button, role).
  // Captures inner text (trimmed), tag name, href (if any), and any
  // data-track / data-testid attribute. Skips text inputs, password
  // fields, file form fields, and anything inside [data-no-track].
  useEffect(() => {
    if (skip) return undefined;
    const FILE_EXT = /\.(pdf|csv|xlsx?|docx?|zip|jpe?g|png|gif|mp3|mp4|mov|webm|json|txt)(\?|$)/i;
    const handleClick = (e) => {
      try {
        const target = e.target.closest("a, button, [role=button], [data-track]");
        if (!target) return;
        // Privacy guards — Sentinel blueprint requires these.
        if (target.closest("[data-no-track]")) return;
        if (target.closest("[data-jtr-block-replay]")) return;
        const isPrivateField = !!target.closest("[data-jtr-private]");
        const isMasked = !!target.closest("[data-jtr-mask]");
        const tag = target.tagName.toLowerCase();
        const href = target.getAttribute("href") || "";
        const isDownload =
          target.hasAttribute("download") ||
          (href && FILE_EXT.test(href)) ||
          (target.getAttribute("data-track-type") || "") === "download";
        // Strip text content from private/masked elements — we capture
        // that the click happened but never the personal data inside.
        const label =
          isPrivateField || isMasked
            ? "[REDACTED]"
            : target.getAttribute("aria-label") ||
              target.getAttribute("data-track-label") ||
              (target.textContent || "").trim().slice(0, 80);
        sendBeacon("event", {
          ...getPayload(),
          event_type: isDownload ? "download" : "click",
          metadata: {
            tag,
            href: href.slice(0, 200),
            label,
            testid: target.getAttribute("data-testid") || "",
            external: href.startsWith("http") && !href.includes(window.location.host),
            // Click coordinates for future heatmap visualization.
            x: e.clientX,
            y: e.clientY,
            viewport_w: window.innerWidth,
            viewport_h: window.innerHeight,
          },
        });
      } catch {
        /* swallow — never let tracking break user clicks */
      }
    };
    document.addEventListener("click", handleClick, { capture: true });
    return () =>
      document.removeEventListener("click", handleClick, { capture: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip]);

  // JS error capture — every unhandled error + promise rejection.
  // Critical for the "what page was the customer on when they hit a bug"
  // pipeline. Send each error individually so the admin Live tab can
  // surface them in near real time.
  useEffect(() => {
    if (skip) return undefined;
    const seen = new Set();
    const handleErr = (ev) => {
      try {
        const msg = (ev.message || ev.reason?.message || String(ev.reason) || "").slice(0, 300);
        const stack = (ev.error?.stack || ev.reason?.stack || "").slice(0, 800);
        const key = msg + ":" + (ev.filename || "") + ":" + (ev.lineno || "");
        if (seen.has(key)) return; // dedupe per page
        seen.add(key);
        sendBeacon("event", {
          ...getPayload(),
          event_type: "js_error",
          metadata: {
            message: msg,
            filename: (ev.filename || "").slice(0, 200),
            lineno: ev.lineno || 0,
            colno: ev.colno || 0,
            stack,
          },
        });
      } catch {
        /* never let error reporting cause more errors */
      }
    };
    window.addEventListener("error", handleErr);
    window.addEventListener("unhandledrejection", handleErr);
    return () => {
      window.removeEventListener("error", handleErr);
      window.removeEventListener("unhandledrejection", handleErr);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip]);

  return null;
}
